import { useEffect, useLayoutEffect, useRef, useState } from "react";
import {
  CaretRightOutlined,
  CheckOutlined,
  CloseOutlined,
  UploadOutlined,
  ExperimentOutlined,
  ExclamationCircleOutlined,
  CloudDownloadOutlined,
  DeleteOutlined,
  EditOutlined,
  ArrowLeftOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import {
  Button,
  Collapse,
  List,
  Space,
  Steps,
  Modal,
  notification,
  Input,
  Spin,
  Tooltip,
} from "antd";
import { useHistory, useParams } from "react-router-dom";
import { useInjectReducer, useInjectSaga } from "redux-injectors";
import { useDispatch, useSelector } from "react-redux";
import { sliceKey, reducer, actions } from "./slice";
import { orderDetailsSaga } from "./saga";
import * as selectors from "./selectors";
import PageHeader from "../../../../components/PageHeader";
import { dateTimeToString } from "../../../../utils/formatter";
import { yupResolver } from "@hookform/resolvers/yup";
import schema from "../../../../schemas/addLabSampleReceptionSchema";
import { useForm } from "react-hook-form";
import { dateToString } from "../../../../utils/formatter";
import "./style.css";
import AddLabSampleReceptionForm from "../../../../forms/AddLabSampleReceptionForm";
import { reverse } from "named-urls";
import routes from "../../../../../routes";
import moment from "moment";
import RejectedDetails from "./components/RejectedDetails";
import RegretDetails from "./components/RegretDetails";
import ViewCorrection from "../../../../components/ViewCorrection";

const { Panel } = Collapse;
const { Step } = Steps;
const { confirm } = Modal;
const { TextArea } = Input;

const ORDER_STATUS = {
  RECEIVED_AT_LAB: "receivedAtLab",
  UNDER_TESTING: "underTesting",
  REPORT_UPLOADED: "reportUploaded",
  REJECTED_BY_LAB: "rejectedByLab",
};

const FileUpload = ({
  name,
  placeholder,
  onChange,
  files,
  handleRemove,
  allowZip = false,
}) => {
  const fileRef = useRef(null);
  const handleFileClick = (e) => {
    e.preventDefault();
    fileRef.current.value = "";
    fileRef.current.click();
  };

  const handleFileChange = (e) => {
    const files = e.target.files;
    onChange(files);
  };

  const getSelectedFiles = () => {
    const boxes = [];
    for (let i = 0; i < files?.length; i++) {
      let file = files.item(i);
      boxes.push(
        <div
          style={{ display: "flex", justifyContent: "space-between" }}
          key={`fileUpload_${i}`}
        >
          <p>{file.name}</p>
          <Button
            onClick={() => {
              fileRef.current.value = "";
              handleRemove();
            }}
            type="text"
            icon={<DeleteOutlined />}
            size="small"
          />
        </div>
      );
    }
    return boxes;
  };

  return (
    <div>
      <input
        ref={fileRef}
        id={name}
        style={{ display: "none" }}
        type="file"
        onChange={handleFileChange}
        accept={allowZip ? ".pdf, .zip" : ".pdf"}
      />
      <Button onClick={handleFileClick} icon={<UploadOutlined />}>
        {placeholder || "Select File"}
      </Button>
      <div style={{ padding: 10 }}>{getSelectedFiles()}</div>
    </div>
  );
};

const PackageBlock = ({ data, sampleVariant }) => {
  //const { sampleCount } = data;
  const { tests } = data;
  const testCategories = {};

  tests.forEach((testObj) => {
    const categoryName = testObj?.testCategory?.name;
    if (!testCategories[testObj?.testCategory?.testCategory_id]) {
      testCategories[testObj.testCategory.testCategory_id] = {
        name: categoryName,
        parameters: [{ name: testObj.parameter, id: testObj._id }],
      };
    } else {
      testCategories[testObj.testCategory.testCategory_id].parameters.push({
        name: testObj.parameter,
        id: testObj._id,
      });
    }
  });

  const commodity = tests[0].commodity;

  const renderTestCategories = (categories) => {
    const rows = [];
    Object.keys(categories).forEach((key, index) => {
      const entry = categories[key];
      const parameters = entry.parameters || [];
      if (parameters.length) {
        rows.push(
          <Panel
            header={
              <span style={{ fontWeight: "bold", color: "#0a8080" }}>
                {`${entry.name} (${parameters.length} tests)`}
              </span>
            }
            key={index}
            style={{
              marginBottom: 20,
              overflow: "hidden",
              backgroundColor: "#eff3f7",
              border: "0px",
              borderRadius: 4,
              color: "#0a8080",
            }}
            id="panelOrderDetailsHeader"
          >
            <List
              itemLayout="horizontal"
              dataSource={parameters}
              renderItem={(item) => (
                <List.Item>
                  <List.Item.Meta
                    title={
                      <span className="orderDetailsTestTitle">{item.name}</span>
                    }
                  />
                </List.Item>
              )}
            />
          </Panel>
        );
      }
    });
    return rows;
  };

  return (
    <div style={{ borderBottom: "2px solid #d1d6de", marginTop: "10px" }}>
      <p>
        <strong>Package:</strong> {data.name}
      </p>

      <div style={{ flex: 1, paddingRight: 40 }}>
        <p>
          <strong>Commodity:</strong> {commodity.name}
        </p>
        <p>
          <strong>Variant:</strong> {sampleVariant}
        </p>
        <div>
          <Collapse
            bordered={false}
            expandIconPosition="right"
            expandIcon={({ isActive }) => (
              <CaretRightOutlined
                style={{ color: "#0a8080" }}
                rotate={isActive ? 90 : 0}
              />
            )}
            style={{ background: "#fff" }}
          >
            {renderTestCategories(testCategories)}
          </Collapse>
          {/*  <p>Sample Count: {sampleCount}</p> */}
        </div>
      </div>
    </div>
  );
};

const OrderDetails = () => {
  const { orderId } = useParams();
  const id = orderId;

  useInjectReducer({ key: sliceKey, reducer: reducer });
  useInjectSaga({ key: sliceKey, saga: orderDetailsSaga });
  const dispatch = useDispatch();
  const history = useHistory();

  const [windowSize, setWindowSize] = useState(null);
  const [rejectionComments, setRejectionComments] = useState(null);
  const [receptionModalDisplay, setReceptionModalDisplay] = useState(false);
  const [rejectModalDisplay, setRejectModalDisplay] = useState(false);
  const [reportModalVisible, setReportModalVisible] = useState(false);
  const [reportFile, setReportFile] = useState(null);
  const [reportError, setReportError] = useState(null);
  const [loaderForModal, setLoaderForModal] = useState(false);
  const [openViewCorrection, setOpenViewCorrection] = useState(false);

  useEffect(() => {
    dispatch(actions.fetchOrderDetails(id));
    dispatch(actions.fetchDropdown(["orderStatus"]));
  }, [dispatch, id]);

  useLayoutEffect(() => {
    function updateWindowSize() {
      setWindowSize(window.innerWidth);
    }
    window.addEventListener("resize", updateWindowSize);
    updateWindowSize();
    return () => window.removeEventListener("resize", updateWindowSize);
  }, []);

  const orderDetails = useSelector(selectors.selectOrderDetails) || {};
  const dropdowns = useSelector(selectors.selectDropdowns);
  const error = useSelector(selectors.selectError);
  const isLoading = useSelector(selectors.selectLoading);
  const { orderStatus = [] } = dropdowns;
  const { packages = [] } = orderDetails;
  let dataForKeyParameterValues = [];

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
    setValue,
    reset,
  } = useForm({
    defaultValues: {
      sampleReceivedDate: "",
      samplePhysicalCondition: "",
      additionalSampleNeeded: "",
      additionalSampleReasons: "",
      sampleRegistrationNumber: "",
      expectedDateOfCompletion: "",
      fssaiLogoPresent: "",
      fssaiLicenseNumber: "",
      brandName: "",
      manufacturingDate: "",
      lotNumber: "",
      vegSymbolPresent: "",
      vegOrNonVeg: "",
      bestBeforeMonths: "",
      expireByMonths: "",
      manufacturingAddress: "",
      jaivikKisanLogoPresent: "",
      countryOfOriginPresent: "",
      countryOfOrigin: "",
    },
    resolver: yupResolver(schema),
  });

  useEffect(() => {
    if (error) {
      notification.error({
        message: error,
      });
      setTimeout(() => {
        dispatch(actions.initializeError());
      }, 1000);
    }
  }, [dispatch, error]);

  useEffect(() => {
    if (orderDetails?.sampleReceivedByLabDetails) {
      let data = {
        ...orderDetails?.sampleReceivedByLabDetails,
        expectedDateOfCompletion: moment(
          orderDetails?.sampleReceivedByLabDetails?.expectedDateOfCompletion,
          "DD/MM/YYYY"
        ),
        sampleReceivedDate: moment(
          orderDetails?.sampleReceivedByLabDetails?.sampleReceivedDate,
          "DD/MM/YYYY hh:mm:ss A"
        ),
      };

      reset(data);
      //reset(orderDetails.sampleReceivedByLabDetails);
    }
  }, [reset, orderDetails.sampleReceivedByLabDetails]);

  const getOrderStatusId = (value) => {
    const status = orderStatus.find((o) => o.value === value);
    if (status) return status._id;
    return null;
  };

  const handleReject = () => {
    dispatch(
      actions.updateOrderStatus({
        id,
        data: {
          status: getOrderStatusId(ORDER_STATUS.REJECTED_BY_LAB),
          reasonForRejectedByLab: rejectionComments
            ? rejectionComments
            : "N.A.",
        },
        onSuccess: () =>
          notification.success({
            message: "Order got rejected successfully!",
          }),
        /*  onFailure: () =>
          notification.error({
            message: "Error Updating Order!",
          }), */
      })
    );

    setRejectModalDisplay(false);
    setRejectionComments(null);
  };
  const handleRejectionCancel = () => {
    setRejectionComments(null);
    setRejectModalDisplay(false);
  };

  const provideSampleReceivedDetailsToSave = (labSampleReceivedDetails) => {
    /*  dataForOrderUpdate.labSampleReceivedDetails = labSampleReceivedDetails;
    dataForOrderUpdate.labSampleReceivedDetails.orderId = orderDetails.orderId;
    dataForOrderUpdate.labSampleReceivedDetails.sampleReceivedDate =
      dateToString(
        dataForOrderUpdate.labSampleReceivedDetails.sampleReceivedDate
      );
    dataForOrderUpdate.labSampleReceivedDetails.expectedDateOfCompletion =
      dateToString(
        dataForOrderUpdate.labSampleReceivedDetails.expectedDateOfCompletion
      ); */

    return {
      ...labSampleReceivedDetails,
      //orderId: orderDetails.orderId,
      sampleReceivedDate: dateTimeToString(
        labSampleReceivedDetails.sampleReceivedDate
      ),
      expectedDateOfCompletion: dateToString(
        labSampleReceivedDetails.expectedDateOfCompletion
      ),
    };
  };
  const handleAccept = (labSampleReceivedDetails = null) => {
    if (!orderDetails.sampleReceivedByLabDetails) {
      confirm({
        title: "Confirm Status Update",
        icon: <ExclamationCircleOutlined />,
        content: `Please click on OK to accept the order #${orderDetails.orderId} `,
        onOk() {
          let dataForOrderUpdate = {};
          dataForOrderUpdate.status = getOrderStatusId(
            ORDER_STATUS.RECEIVED_AT_LAB
          );
          if (labSampleReceivedDetails) {
            dataForOrderUpdate.labSampleReceivedDetails =
              provideSampleReceivedDetailsToSave(labSampleReceivedDetails);
          }
          setLoaderForModal(true);
          dispatch(
            actions.updateOrderStatus({
              id,
              data: dataForOrderUpdate,
              onSuccess: () => {
                notification.success({
                  message: "Order got accepted successfully!",
                });
                reset();
                setReceptionModalDisplay(false);
                setLoaderForModal(false);
              },
              onFailure: () => {
                notification.error({
                  message: "Error Updating Order!",
                });
                setLoaderForModal(false);
              },
            })
          );
        },
        onCancel() {},
      });
    } else {
      //update lab sample received details
      let payload = {
        data: {
          labSampleReceivedDetails: provideSampleReceivedDetailsToSave(
            labSampleReceivedDetails
          ),
        },
        onSuccess: () => {
          notification.success({
            message: "Sample Received Details Got Updated Successfully!!!",
          });
          setReceptionModalDisplay(false);
          setLoaderForModal(false);
        },
        onFailure: () => {
          setLoaderForModal(false);
        },
      };
      dispatch(actions.updateSampleReceived(payload));
    }
  };

  const handleUnderTesting = () => {
    confirm({
      title: "Confirm Status Update",
      icon: <ExclamationCircleOutlined />,
      content: `Please click on OK to update the order #${orderDetails.orderId} to UNDER TESTING`,
      onOk() {
        dispatch(
          actions.moveToUnderTesting({
            orderId: orderDetails.orderId,
            statusId: getOrderStatusId(ORDER_STATUS.UNDER_TESTING),
            onSuccess: () => {
              notification.success({
                message: "Order got updated to Under Testing successfully!",
              });
              history.goBack();
            },
            onFailure: () =>
              notification.error({
                message: "Error Updating Order!",
              }),
          })
        );
      },
      onCancel() {
        // console.log("Cancel");
      },
    });
  };

  dataForKeyParameterValues = packages[0]?.package?.keyParameters?.map(
    (obj) => {
      let requiredObj = { parameter: obj.parameter };
      if (orderDetails.labResponseId) {
        let valueObj = orderDetails?.labResponseId?.keyParametersValues?.find(
          (el) => el.parameter === obj.parameter
        );
        requiredObj.parameterValue = valueObj?.parameterValue;
      } else {
        requiredObj.parameterValue = "";
      }
      return requiredObj;
    }
  );

  const handleReportUploadSuccess = () => {
    setReportModalVisible(false);
    setReportFile(null);
    notification.success({
      message: "Report uploaded successfully!!",
    });
  };

  const handleReportUploadFailure = () => {
    setReportModalVisible(false);
    setReportFile(null);
    notification.error({
      message: "There was some error while uploading Report",
    });
  };
  const handleCancelReportUpload = () => {
    setReportModalVisible(false);
    setReportFile(null);
    setReportError(null);
  };
  const handleUploadReport = () => {
    // take file
    if (reportFile?.length) {
      const payloadData = {
        file: reportFile[0],
        id: orderDetails._id,
        source: "Order Details",
        fileUsedFor: "Lab Reports",
        companyId: orderDetails.company._id,
        orderId: orderId,
        onSuccess: handleReportUploadSuccess,
        onFailure: handleReportUploadFailure,
      };
      payloadData.status = getOrderStatusId(ORDER_STATUS.REPORT_UPLOADED);

      dispatch(actions.uploadReport(payloadData));
    } else {
      setReportFile(null);
    }
  };

  const handleFileChange = (files) => {
    const acceptedFilesExtension = [
      "zip",
      "application/pdf",
      "application/octet-stream",
      "application/zip",
      "application/x-zip",
      "application/x-zip-compressed",
    ];
    if (acceptedFilesExtension.indexOf(files[0]?.type) === -1) {
      setReportError(true);
      setReportFile(null);
      return;
    } else {
      setReportError(null);
      setReportFile(files);
    }
  };

  const handleRemoveFile = () => {
    setReportFile(null);
  };

  const handleDownloadReport = () => {
    dispatch(
      actions.downloadFile({
        id,
        data: { fileName: orderDetails.labResponseId.reportFile },
      })
    );
  };

  const handleDownloadMetadata = () => {
    dispatch(
      actions.downloadFile({
        id,
        data: { fileName: orderDetails.labResponseId.reportMetadataFile },
      })
    );
  };

  const getProgressStatus = () => {
    const statusTrail = orderDetails?.statusTrail || [];
    const timelineForOrdersNotRejectedByLab = [
      "Pending",
      "Received at Lab",
      "Under Testing",
      "Report Uploaded",
      "Completed",
    ];

    const timelineForOrdersRejectedByLab = ["Pending", "Rejected By Lab"];

    let timeline = null;
    let currentProgress = null;

    if (orderDetails?.status?.value === "regret") {
      const timelineForOrdersRegret = [
        // ...new Set(
         ...(orderDetails?.statusTrail?.map((trail) => trail.status?.label) || [])
        // ),
      ];
      
      const index = timelineForOrdersRegret.indexOf("Delivered to Lab");

      if (index !== -1) {
        timelineForOrdersRegret.splice(0, index + 1);
      }

      timeline = ["Pending", ...timelineForOrdersRegret];

      currentProgress = timelineForOrdersRegret.length;
    } else if (orderDetails?.status?.value === "rejectedByLab") {
      timeline = timelineForOrdersRejectedByLab;
      currentProgress = 1;
    } else if (orderDetails?.status?.value === "underDispute") {
      timelineForOrdersNotRejectedByLab.splice(4, 0, "Under Dispute");
      timeline = timelineForOrdersNotRejectedByLab;
      currentProgress = 4;
    } else {
      timeline = timelineForOrdersNotRejectedByLab;
      switch (orderDetails?.status?.value) {
        case "receivedAtLab":
          currentProgress = 1;
          break;
        case "underTesting":
          currentProgress = 2;
          break;
        case "reportUploaded":
          currentProgress = 3;
          break;
        case "completed":
          currentProgress = 4;
          break;
        case "disputeResolved":
          currentProgress = 3;
          break;
        case "underDispute":
          currentProgress = 3;
          break;
        default:
          currentProgress = 0;
          break;
      }
    }

    // const timelineWithTimestamps = timeline.map((step, index) => {
    //   const matchingStatus = [...statusTrail]
    //     ?.reverse()
    //     .find((obj) => obj.status.label === step);

    //   return {
    //     title: step,
    //     timestamp: matchingStatus?.updatedAt
    //       ? dateToString(matchingStatus.updatedAt)
    //       : null,
    //   };
    // });

    return { timeline, currentProgress };
  };

  const handleSaveSampleReceptionDetails = (data) => {
    console.log(data);
    handleAccept(data);
  };

  const handleCancelSampleReceptionDetails = () => {
    setReceptionModalDisplay(false);
    reset();
  };

  const { currentProgress, timeline } = getProgressStatus();
  const isDisabledMoveToUnderTesting =
    !orderDetails?.sampleReceivedByLabDetails?.labelingComplianceDone;

  const requestCorrections =
    orderDetails?.underDispute?.at(-1)?.requestCorrections?.map((correction)=> correction.type) || [];
  const LabCorrectionPresent = orderDetails?.underDispute;

  return (
    <div>
      <PageHeader
        title={
          <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <ArrowLeftOutlined
              onClick={() => history.goBack()}
              className="back-icon"
            />
            {`Order #${orderDetails?.orderId || ""}`}
          </span>
        }
        actions={
          <Space>
            <div className="labOrderDetailsHeaderButtons">
              {orderDetails?.status?.value === "deliveredToLab" && (
                <>
                  {/*   <Button
                      size={windowSize > 650 ? "middle" : "small"}
                      id="labButtonsHeader"
                      onClick={() => {
                        setRejectModalDisplay(true);
                      }}
                    >
                      <CloseOutlined /> REJECT
                    </Button> */}
                  <Button
                    size={windowSize > 650 ? "middle" : "small"}
                    id="labButtonsHeader"
                    onClick={() => {
                      //handleAccept();
                      //setReceptionModalDisplay(true);
                      //setLoaderForModal(false);
                      history.push(
                        reverse(
                          `${routes.labs.orders.details.sampleReceived}`,
                          {
                            orderId: id,
                          }
                        )
                      );
                    }}
                    type="primary"
                  >
                    <CheckOutlined /> Accept
                  </Button>
                </>
              )}
              {["receivedAtLab", "underTesting", "reportUploaded"].includes(
                orderDetails?.status?.value
              ) && (
                <Button
                  size={windowSize > 650 ? "middle" : "small"}
                  id="labButtonsHeader"
                  onClick={() => {
                    history.push(
                      reverse(`${routes.labs.orders.details.sampleReceived}`, {
                        orderId: id,
                      })
                    );
                  }}
                  type="primary"
                >
                  <EditOutlined /> Update Labelling Details
                </Button>
              )}
              {orderDetails?.status?.value === "receivedAtLab" && (
                <Tooltip
                  title={
                    isDisabledMoveToUnderTesting
                      ? "Please Fill the Labelling details first"
                      : "Click to update status"
                  }
                >
                  <Button
                    size={windowSize > 650 ? "middle" : "small"}
                    id="labButtonsHeader"
                    onClick={() => {
                      handleUnderTesting();
                    }}
                    type="primary"
                    disabled={isDisabledMoveToUnderTesting}
                  >
                    <ExperimentOutlined /> Move to Under Testing
                  </Button>
                </Tooltip>
              )}
              {orderDetails?.status?.value === "underTesting" && (
                <Button
                  size={windowSize > 650 ? "middle" : "small"}
                  id="labButtonsHeader"
                  onClick={() => {
                    //setReportModalVisible(true);
                    history.push(
                      reverse(`${routes.labs.orders.details.uploadReport}`, {
                        orderId: id,
                      })
                    );
                  }}
                  type="primary"
                >
                  <UploadOutlined /> Upload Report
                </Button>
              )}
              {(orderDetails?.status?.value === "reportUploaded" ||
                orderDetails?.status?.value === "completed") && (
                <>
                  {LabCorrectionPresent && (
                    <Button
                      size={windowSize > 650 ? "middle" : "small"}
                      id="labButtonsHeader"
                      onClick={() => setOpenViewCorrection(true)}
                      type="primary"
                    >
                      <EyeOutlined /> View Correction Request
                    </Button>
                  )}
                  <Button
                    size={windowSize > 650 ? "middle" : "small"}
                    id="labButtonsHeader"
                    onClick={handleDownloadMetadata}
                    type="primary"
                  >
                    <CloudDownloadOutlined /> Download Metadata
                  </Button>

                  <Button
                    size={windowSize > 650 ? "middle" : "small"}
                    id="labButtonsHeader"
                    onClick={handleDownloadReport}
                    type="primary"
                  >
                    <CloudDownloadOutlined /> Download Report
                  </Button>
                </>
              )}

              {orderDetails?.status?.value === "underDispute" && (
                <>
                  <Button
                    size={windowSize > 650 ? "middle" : "small"}
                    id="labButtonsHeader"
                    onClick={() => setOpenViewCorrection(true)}
                    type="primary"
                  >
                    <EyeOutlined /> View Correction Request
                  </Button>

                  {(requestCorrections?.includes("acceptanceForm") ||
                    requestCorrections?.includes("labellingForm")) && (
                    <Button
                      size={windowSize > 650 ? "middle" : "small"}
                      id="labButtonsHeader"
                      onClick={() => {
                        history.push(
                          reverse(
                            `${routes.labs.orders.details.sampleReceived}`,
                            {
                              orderId: id,
                            }
                          )
                        );
                      }}
                      type="primary"
                    >
                      <EditOutlined /> Update Labelling Details
                    </Button>
                  )}
                  {requestCorrections?.includes("report") && <Button
                    size={windowSize > 650 ? "middle" : "small"}
                    id="labButtonsHeader"
                    onClick={() => {
                      //setReportModalVisible(true);
                      history.push(
                        reverse(
                          `${routes.labs.orders.details.reuploadReport}`,
                          {
                            orderId: id,
                          }
                        )
                      );
                    }}
                    type="primary"
                  >
                    <UploadOutlined /> Re-upload Report
                  </Button>}
                </>
              )}
            </div>
          </Space>
        }
      />
      {isLoading ? (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "75vh",
            backgroundColor: "#fff",
          }}
        >
          <Spin size="large" />
        </div>
      ) : (
        <>
          <div className="labOrderDetailsProgressContainer">
            <Steps
              progressDot
              responsive="true"
              size="small"
              className="labOrderDetailsSteps"
              current={currentProgress}
            >
              {timeline.map((title) => (
                <Step title={title} />
              ))}
            </Steps>
          </div>

          {orderDetails?.status?.value === "rejectedByLab" ? (
            <RejectedDetails orderDetails={orderDetails} />
          ) : null}

          {orderDetails?.status?.value === "regret" ? (
            <RegretDetails orderDetails={orderDetails} />
          ) : null}

          <div className="labOrderDetailsUpperSection">
            <div className="labOrderDetailsPackageSection">
              <p className="labOrderDetailsContentHeader">
                Order Inclusions{" "}
                <span style={{ fontStyle: "italic" }}>**(Refer to FSSR)**</span>
              </p>

              <div className="labOrderDetailsPackageSectionContent">
                {packages?.map((packageDetails) => (
                  <PackageBlock
                    key={packageDetails._id}
                    data={packageDetails}
                    sampleVariant={orderDetails.sampleVariant}
                  />
                ))}
              </div>
            </div>
            <div className="labOrderDetailsSummary">
              <p className="labOrderDetailsContentHeader">Order Summary</p>
              <div className="labOrderDetailsSummaryContent">
                <div className="labOrderDetailsSummaryContentRow">
                  <p className="labOrderDetailsSummaryContentItems">
                    {" "}
                    Order Id
                  </p>
                  <p className="labOrderDetailsSummaryContentItems">
                    {" "}
                    {orderDetails?.orderId}
                  </p>
                </div>
                <div className="labOrderDetailsSummaryContentRow">
                  <p className="labOrderDetailsSummaryContentItems"> Company</p>
                  <p className="labOrderDetailsSummaryContentItems">
                    {orderDetails?.company?.companyName}
                  </p>
                </div>
                <div className="labOrderDetailsSummaryContentRow">
                  <p className="labOrderDetailsSummaryContentItems">
                    QR Code Id
                  </p>
                  <p
                    className="labOrderDetailsSummaryContentItems"
                    style={{ textOverflow: "ellipsis" }}
                  >
                    {orderDetails?.sampleDetailsId?.qrCodeId}
                  </p>
                </div>

                <div className="labOrderDetailsSummaryContentRow">
                  <p className="labOrderDetailsSummaryContentItems">
                    Order Creation Date
                  </p>
                  <p className="labOrderDetailsSummaryContentItems">
                    {dateTimeToString(orderDetails?.createdAt).split(" ")[0]}
                  </p>
                </div>
                {orderDetails?.status?.value === "reportUploaded" && (
                  <>
                    <div className="labOrderDetailsSummaryContentRow">
                      <p className="labOrderDetailsSummaryContentItems">
                        Overall Safety Compliant
                      </p>
                      <p className="labOrderDetailsSummaryContentItems">
                        {orderDetails?.labResponseId?.overallSafetyCompliant}
                      </p>
                    </div>
                    <div className="labOrderDetailsSummaryContentRow">
                      <p className="labOrderDetailsSummaryContentItems">
                        Overall Quality Compliant
                      </p>
                      <p className="labOrderDetailsSummaryContentItems">
                        {orderDetails?.labResponseId?.overallQualityCompliant}
                      </p>
                    </div>
                    {orderDetails?.sampleVariant !== "Loose" && (
                      <div className="labOrderDetailsSummaryContentRow">
                        <p className="labOrderDetailsSummaryContentItems">
                          Overall Labelling Compliant
                        </p>
                        <p className="labOrderDetailsSummaryContentItems">
                          {
                            orderDetails?.labResponseId
                              ?.overallLabellingCompliant
                          }
                        </p>
                      </div>
                    )}

                    <div className="labOrderDetailsSummaryContentRow">
                      <p className="labOrderDetailsSummaryContentItems">
                        Overall Sample Classification
                      </p>
                      <p className="labOrderDetailsSummaryContentItems">
                        {
                          orderDetails?.labResponseId
                            ?.overallSampleClassification
                        }
                      </p>
                    </div>
                  </>
                )}
                <p
                  style={{
                    fontWeight: "bold",
                    fontSize: "16px",
                    marginTop: "20px",
                  }}
                >
                  Key Parameters{" "}
                  <span style={{ fontStyle: "italic" }}>
                    **(Only for Information Purposes)**
                  </span>
                </p>

                {packages.length && dataForKeyParameterValues.length > 0 ? (
                  dataForKeyParameterValues.map((el, index) => (
                    <div
                      key={el.index}
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        marginBottom: "8px",
                      }}
                    >
                      <span>{el.parameter}</span>
                      <span>{el.parameterValue}</span>
                    </div>
                  ))
                ) : (
                  <div></div>
                )}
                {/*orderDetails?.status?.value !== "reportUploaded" &&*/}
                {/* !orderDetails?.labResponseId &&
              orderDetails?.packages[0]?.package?.keyParameters?.map((el) => (
                <div
                  key={el._id}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    marginBottom: "8px",
                  }}
                >
                  <span>{el.parameter}</span>
                </div>
              )) */}
              </div>
            </div>
          </div>
          {/*       <div className="labOrderDetailsLowerSection">
        <div className="labOrderDetailsShippingSection">
          <p className="labOrderDetailsContentHeader">Key Parameters</p>
          <div className="labOrderDetailsShippingSectionContent">
                 <p>Shipping details available soon!!!</p> 
          </div>
        </div>
      </div> */}

          <Modal
            title="Update Order Status"
            visible={rejectModalDisplay}
            onCancel={handleRejectionCancel}
            onOk={handleReject}
          >
            <h2>Are you sure to reject the order?</h2>
            <p>
              <strong>Reasons for Rejection:</strong>
            </p>
            <TextArea
              rows={4}
              value={rejectionComments}
              onChange={(e) => {
                setRejectionComments(e.target.value);
              }}
            />
          </Modal>
          <Modal
            title="Sample Reception Form"
            visible={receptionModalDisplay}
            onCancel={handleCancelSampleReceptionDetails}
            onOk={handleSubmit(handleSaveSampleReceptionDetails)}
            okButtonProps={{ disabled: loaderForModal }}
            width={1200}
          >
            {loaderForModal ? (
              <Spin
                size="medium"
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              />
            ) : (
              <AddLabSampleReceptionForm
                control={control}
                watch={watch}
                errors={errors}
                setValue={setValue}
              />
            )}
          </Modal>
          <Modal
            title="Upload Report"
            visible={reportModalVisible}
            onOk={handleUploadReport}
            okButtonProps={{
              disabled: !reportFile ? true : false,
            }}
            okText="Upload"
            onCancel={handleCancelReportUpload}
          >
            <FileUpload
              placeholder="Select .zip file or .pdf file with Report(s)"
              allowZip={true}
              onChange={handleFileChange}
              name="report"
              files={reportFile}
              handleRemove={handleRemoveFile}
            />
            {reportError && (
              <p
                style={{
                  color: "red",
                  fontSize: "14px",
                  marginLeft: "8px",
                }}
              >
                Please upload .pdf or .zip files only
              </p>
            )}
          </Modal>
          {openViewCorrection && (
            <ViewCorrection
              onOpen={openViewCorrection}
              onClose={() => setOpenViewCorrection(false)}
              id={id}
            />
          )}
        </>
      )}
    </div>
  );
};

export default OrderDetails;
